import { useEffect, useMemo, useState } from "react";
import { Navigate, useNavigate, useParams, useSearchParams } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { Check, ChevronDown, Repeat, Trash2 } from "lucide-react";
import { toast } from "sonner";
import {
  formatMoney,
  Timestamp,
  uuid,
  type ExpenseEntry,
  type Trip,
} from "@travelix/core";

import { useTrip } from "@/hooks/use-trip";
import { useTripMutation } from "@/hooks/use-trip-mutation";
import { useAuth } from "@/lib/auth-provider";
import { isExpenseLoggingClosed } from "@/lib/trip-readonly";
import { logAnalyticsEvent } from "@/lib/analytics";
import { parseAmount, currencySymbol, todayLocalYmd, parseYmdLocalNoon } from "@/lib/money-input";
import { Sheet, SheetContent, SheetHeader, SheetBody, SheetFooter } from "@/components/ui/sheet";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Field } from "@/components/ui/field";
import { Avatar } from "@/components/ui/avatar";
import { Skeleton } from "@/components/ui/skeleton";
import { ConfirmDialog } from "@/components/ui/dialog";
import { cn } from "@/lib/utils";
import {
  addExpenseToTrip,
  displayNameOf,
  removeExpenseFromTrip,
  updateExpenseInTrip,
  unwrap,
  queryKeys,
} from "@/lib/api";

/**
 * A minimal sibling of AddExpenseRoute for logging a settlement between
 * two trip members — no split editor, no category, no FX. Persisted as
 * an ExpenseEntry with type "payment": payer is who's paying, and the
 * whole amount is split to just the recipient. calculateTripBalances
 * already sums paid/owes generically, so this shifts balances the same
 * way a full payment would, with the amount left editable so a partial
 * settlement (friend pays back $1,000 of a $3,000 debt) is a one-field
 * edit instead of a "misc" expense workaround.
 */
export default function RecordPaymentRoute() {
  const navigate = useNavigate();
  const { id } = useParams<{ id: string }>();
  const { data: trip, isLoading } = useTrip(id);
  const { user } = useAuth();
  const [searchParams] = useSearchParams();
  const editId = searchParams.get("editId");

  const existing = useMemo<ExpenseEntry | null>(
    () =>
      editId && trip
        ? (trip.expenses ?? []).find((e) => e.id === editId && e.type === "payment") ?? null
        : null,
    [editId, trip],
  );
  const isEdit = !!editId;

  // Same grace-period rule as regular expenses. Bounce direct URL access
  // with a soft toast; the redirect happens below all hooks (see the
  // Navigate return before the JSX) so hooks never run conditionally.
  const readOnly = isExpenseLoggingClosed(trip);
  useEffect(() => {
    if (readOnly && id) {
      toast.info("The window to add or edit expenses for this trip has closed.");
    }
  }, [readOnly, id]);

  const close = () => navigate(`/trip/${id}/expenses`, { replace: true });

  const members = useMemo(
    () =>
      trip
        ? Object.entries(trip.members).map(([uid, m]) => ({ userId: uid, userName: m.name }))
        : [],
    [trip],
  );

  const [fromUid, setFromUid] = useState<string | null>(null);
  const [toUid, setToUid] = useState<string | null>(null);
  const [amount, setAmount] = useState("");
  const [dateYmd, setDateYmd] = useState<string>(() => todayLocalYmd());
  const [note, setNote] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [confirmDeleteOpen, setConfirmDeleteOpen] = useState(false);

  // Prefill from the existing entry (edit) or from ?from=&to=&amount=
  // (new, tapped off a settle-up suggestion). Guarded so later renders
  // don't clobber the user's edits.
  const [hydrated, setHydrated] = useState(false);
  useEffect(() => {
    if (hydrated || !trip) return;
    if (existing) {
      setFromUid(existing.payerUid ?? existing.creatorId);
      setToUid(existing.splits[0]?.userId ?? null);
      setAmount(String(existing.amount));
      setNote(existing.name === "Payment" ? "" : existing.name);
      try {
        const d = existing.expenseDate.toDate();
        const y = d.getFullYear();
        const m = String(d.getMonth() + 1).padStart(2, "0");
        const day = String(d.getDate()).padStart(2, "0");
        setDateYmd(`${y}-${m}-${day}`);
      } catch {
        // Keep default (today) if the timestamp can't be converted.
      }
      setHydrated(true);
    } else if (!editId) {
      const qFrom = searchParams.get("from");
      const qTo = searchParams.get("to");
      const qAmount = searchParams.get("amount");
      if (qFrom && trip.members[qFrom]) setFromUid(qFrom);
      if (qTo && trip.members[qTo]) setToUid(qTo);
      if (qAmount) {
        const n = parseFloat(qAmount);
        if (Number.isFinite(n) && n > 0) setAmount(n.toFixed(2));
      }
      setHydrated(true);
    }
  }, [existing, trip, hydrated, editId, searchParams]);

  const currency = trip?.currency ?? "EUR";
  const amountNum = parseAmount(amount);

  const swap = () => {
    setFromUid(toUid);
    setToUid(fromUid);
  };

  const mutation = useTripMutation<ExpenseEntry, void, Trip>({
    keys: [queryKeys.trip(id ?? "")],
    mutationFn: async (entry) => {
      if (!trip) throw new Error("Not ready");
      const { error } = isEdit
        ? await updateExpenseInTrip(trip.id, entry)
        : await addExpenseToTrip(trip.id, entry);
      if (error) throw error;
    },
    applyOptimistic: (_keys, entry) => [
      (prev) => {
        if (!prev) return prev;
        if (isEdit) {
          return {
            ...prev,
            expenses: (prev.expenses ?? []).map((e) => (e.id === entry.id ? entry : e)),
          };
        }
        return { ...prev, expenses: [entry, ...(prev.expenses ?? [])] };
      },
    ],
  });

  const deleter = useTripMutation<string, void, Trip>({
    keys: [queryKeys.trip(id ?? "")],
    mutationFn: async (expenseId) => {
      if (!trip) throw new Error("Not ready");
      await unwrap(removeExpenseFromTrip(trip.id, expenseId));
    },
    applyOptimistic: (_keys, expenseId) => [
      (prev) =>
        prev
          ? { ...prev, expenses: (prev.expenses ?? []).filter((e) => e.id !== expenseId) }
          : prev,
    ],
    successToast: "Payment removed.",
  });

  const buildEntry = (): ExpenseEntry | null => {
    if (!trip) return null;
    if (!Number.isFinite(amountNum) || amountNum <= 0) {
      setError("Enter an amount above zero.");
      return null;
    }
    if (!fromUid || !toUid) {
      setError("Choose who paid and who received it.");
      return null;
    }
    if (fromUid === toUid) {
      setError("Pick two different people.");
      return null;
    }
    const fromMember = members.find((m) => m.userId === fromUid);
    const toMember = members.find((m) => m.userId === toUid);
    if (!fromMember || !toMember) {
      setError("Couldn't find those trip members.");
      return null;
    }
    const now = Timestamp.now();
    // Unlike regular expenses, payerUid/payerName are always set
    // explicitly — balance correctness depends on unambiguously
    // knowing who the "from" person is, regardless of which side of
    // the payment is the one logging it.
    return {
      id: existing?.id ?? uuid(),
      name: note.trim() || "Payment",
      amount: amountNum,
      currency,
      expenseDate: Timestamp.fromDate(parseYmdLocalNoon(dateYmd)),
      type: "payment",
      creatorId: existing?.creatorId ?? user?.uid ?? fromMember.userId,
      creatorName:
        existing?.creatorName ?? (user ? displayNameOf(user, fromMember.userName) : fromMember.userName),
      payerUid: fromMember.userId,
      payerName: fromMember.userName,
      splitMode: "exact",
      splits: [
        {
          userId: toMember.userId,
          userName: toMember.userName,
          amount: amountNum,
          percentage: null,
          currency,
        },
      ],
      createdAt: existing?.createdAt ?? now,
      updatedAt: now,
    };
  };

  const submit = () => {
    const entry = buildEntry();
    if (!entry) return;
    mutation.mutate(entry, {
      onSuccess: () => {
        toast.success(isEdit ? "Payment updated." : "Payment recorded.", {
          description: `${entry.payerName} → ${entry.splits[0]?.userName} · ${formatMoney(entry.amount, currency)}`,
        });
        if (!isEdit) {
          logAnalyticsEvent("settlement_recorded", {
            trip_id: trip?.id ?? "",
            currency,
          });
        }
        close();
      },
      onError: (err) => {
        setError(err instanceof Error ? err.message : "Couldn't save the payment.");
      },
    });
  };

  const confirmDelete = async () => {
    if (!existing) return;
    await deleter.mutateAsync(existing.id);
    setConfirmDeleteOpen(false);
    close();
  };

  // Read-only bounce — must live below all hook calls above.
  if (readOnly && id) {
    return <Navigate to={`/trip/${id}/expenses`} replace />;
  }

  return (
    <>
      <Sheet open guardUnsaved onOpenChange={(open) => !open && !mutation.isPending && close()}>
        <SheetContent>
          <SheetHeader title={isEdit ? "Edit payment" : "Record a payment"} />

          <form
            onSubmit={(e) => {
              e.preventDefault();
              submit();
            }}
            className="flex min-h-0 flex-1 flex-col"
          >
            <SheetBody className="flex flex-col gap-4">
              {isLoading || !trip ? (
                <Skeleton className="h-40 w-full rounded-md" />
              ) : (
                <>
                  {/* Amount — trip currency only, no FX switcher (a
                      settlement is already in the trip's currency). */}
                  <div className="border-b border-line pb-4">
                    <div className="flex items-baseline gap-2">
                      <span className="tabular text-xl font-semibold text-ink-muted">
                        {currencySymbol(currency)}
                      </span>
                      <Input
                        type="text"
                        inputMode="decimal"
                        placeholder="0.00"
                        value={amount}
                        onChange={(e) => {
                          setAmount(e.target.value.replace(/[^\d.,]/g, ""));
                          if (error) setError(null);
                        }}
                        className="!h-auto border-0 !p-0 !text-4xl !font-bold !tracking-tight tabular focus:!shadow-none"
                        autoFocus
                        invalid={!!error && amountNum <= 0}
                      />
                    </div>
                  </div>

                  {/* From / To — who's paying, who's receiving */}
                  <div className="flex flex-col gap-1">
                    <MemberPickerRow
                      label="From"
                      members={members}
                      value={fromUid}
                      onChange={(uid) => {
                        setFromUid(uid);
                        if (error) setError(null);
                      }}
                      excludeUid={toUid}
                    />
                    <div className="flex items-center justify-center border-t border-line py-1">
                      <button
                        type="button"
                        onClick={swap}
                        aria-label="Swap from and to"
                        className="flex h-7 w-7 items-center justify-center rounded-full text-ink-subtle hover:bg-bg-sunken hover:text-ink"
                      >
                        <Repeat className="h-3.5 w-3.5" />
                      </button>
                    </div>
                    <MemberPickerRow
                      label="To"
                      members={members}
                      value={toUid}
                      onChange={(uid) => {
                        setToUid(uid);
                        if (error) setError(null);
                      }}
                      excludeUid={fromUid}
                    />
                  </div>

                  {/* When — freeform, not clamped to the trip's own
                      dates: a settlement can happen before or after
                      the trip itself. */}
                  <div className="flex items-center justify-between border-t border-line pt-4">
                    <label htmlFor="pay-date" className="text-[13px] font-medium text-ink-muted">
                      When
                    </label>
                    <Input
                      id="pay-date"
                      type="date"
                      value={dateYmd}
                      onChange={(e) => setDateYmd(e.target.value)}
                      className="max-w-[180px] !text-[15px] !font-semibold !text-ink"
                    />
                  </div>

                  <Field id="pay-note" label="Note (optional)">
                    <Input
                      id="pay-note"
                      placeholder="Cash back for dinner"
                      value={note}
                      onChange={(e) => setNote(e.target.value)}
                    />
                  </Field>

                  {error ? <p className="text-[12px] text-danger m-0">{error}</p> : null}
                </>
              )}
            </SheetBody>
            <SheetFooter>
              <Button type="submit" block disabled={mutation.isPending || !trip}>
                {mutation.isPending ? "Saving…" : isEdit ? "Save changes" : "Record payment"}
              </Button>
              {isEdit ? (
                <button
                  type="button"
                  onClick={() => setConfirmDeleteOpen(true)}
                  disabled={mutation.isPending || deleter.isPending}
                  className="inline-flex items-center justify-center gap-1.5 rounded-md py-2 text-[13px] font-semibold text-danger hover:bg-danger-soft/40 disabled:opacity-50"
                >
                  <Trash2 className="h-4 w-4" />
                  Delete payment
                </button>
              ) : null}
              <Button
                type="button"
                variant="ghost"
                block
                onClick={close}
                disabled={mutation.isPending}
              >
                Cancel
              </Button>
            </SheetFooter>
          </form>
        </SheetContent>
      </Sheet>

      {isEdit && existing ? (
        <ConfirmDialog
          open={confirmDeleteOpen}
          onOpenChange={setConfirmDeleteOpen}
          title="Delete this payment?"
          description="It's removed for everyone. Balances recalculate."
          confirmLabel="Delete"
          destructive
          onConfirm={confirmDelete}
        />
      ) : null}
    </>
  );
}

// ─── From/To member picker ─────────────────────────────────────
function MemberPickerRow({
  label,
  members,
  value,
  onChange,
  excludeUid,
}: {
  label: string;
  members: { userId: string; userName: string }[];
  value: string | null;
  onChange: (uid: string) => void;
  excludeUid?: string | null;
}) {
  const [open, setOpen] = useState(false);
  const selected = members.find((m) => m.userId === value);
  const options = members.filter((m) => m.userId !== excludeUid);

  return (
    <div>
      <button
        type="button"
        className="flex w-full items-center justify-between py-2"
        aria-expanded={open}
        aria-label={`Change ${label.toLowerCase()}`}
        onClick={() => setOpen((v) => !v)}
      >
        <span className="text-[13px] font-medium text-ink-muted">{label}</span>
        <span className="inline-flex items-center gap-1.5 text-[15px] font-semibold text-ink">
          {selected ? selected.userName : "Select"}
          <ChevronDown
            className={cn("h-4 w-4 text-ink-subtle transition-transform", open && "rotate-180")}
          />
        </span>
      </button>
      <AnimatePresence initial={false}>
        {open ? (
          <motion.ul
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: "auto", opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.2, ease: "easeOut" }}
            role="radiogroup"
            aria-label={label}
            className="overflow-hidden"
          >
            {options.map((m, i) => {
              const sel = value === m.userId;
              return (
                <li key={m.userId}>
                  <button
                    type="button"
                    role="radio"
                    aria-checked={sel}
                    onClick={() => {
                      onChange(m.userId);
                      setOpen(false);
                    }}
                    className={cn(
                      "mt-2 flex w-full items-center gap-3 rounded-md px-2 py-2 text-left transition-colors",
                      sel ? "bg-primary-soft" : "hover:bg-bg-sunken",
                    )}
                  >
                    <Avatar name={m.userName} colorSeed={i} size="sm" />
                    <span className="flex-1 text-[14px] text-ink">{m.userName}</span>
                    {sel ? <Check className="h-4 w-4 text-primary" strokeWidth={2.5} /> : null}
                  </button>
                </li>
              );
            })}
          </motion.ul>
        ) : null}
      </AnimatePresence>
    </div>
  );
}
